import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Check, 
  Calendar, 
  Users, 
  Sparkles,
  MapPin,
  Clock,
  Monitor,
  HandHeart,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Star,
  Loader2,
  Lock,
  Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const iconMap = {
  monitor: Monitor,
  map_pin: MapPin,
  users: Users,
  clock: Clock,
  calendar: Calendar
};

const FacilitadorasLanding = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const courseSlug = slug || 'facilitadoras-sosten-energetico'; // Default for backward compatibility
  const { isAuthenticated } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Testimonios de Facilitadoras
  const facilitadorasTestimonials = [
    { 
      name: "Ana María", 
      highlight: "La mejor formación que has hecho",
      text: "De entrada agradecerte el regalazo que nos has hecho con esta formación. Clarísimamente para mí es la mejor que has hecho. Me estoy recalibrando. Integrando mucho el tubo de luz… siento más paz, más paz. He bajado revoluciones. Se dan situaciones que en otro momento sería más actuadora y ahora… no siempre hace falta desde el hacer, sino el estar. Y desde dónde se está. Irradiar sin reaccionar… y eso es lo que me agota, lo que me drena. Muchísimas gracias."
    },
    { 
      name: "Maru", 
      highlight: "Hay para tiempo. Lo es todo.",
      text: "Primero agradecerte… todo lo que yo he tocado contigo… con esto hay para tiempo. Y es básico… a la vez que es básico lo es todo. Yo entro y entro y parece que no he entrado antes. Lo leí ayer y hoy es como si fuera la primera vez. Me han servido mucho las frases… son mis básicos de la mañana sí o sí. Tubo de luz, punto cero, anclar… y siento que esto está ahí para tiempo."
    },
    { 
      name: "Rosalía", 
      highlight: "Empapar la lluvia: integrar desde el sentir",
      text: "Gracias por compartirlo y por mostrarlo al mundo. Cada vez que abro el módulo lo habré leído cuatro veces y es como si fuese la primera vez… y al tercer día pensé: no, esto es así. Lo enfoqué desde dejarme sentir más que querer atrapar conceptos… como el que empapa la lluvia. Me ha ayudado mucho ver la geometría y nosotras dentro… 'estamos todas ahí' y eso me ha dado potencia, fuerza. Me veo más consciente de cómo reacciona mi cuerpo y puedo parar y decir: geometría, tubo… y tomar perspectiva desde el punto cero. Muy agradecida."
    },
    { 
      name: "Xisca", 
      highlight: "Más paz, más armonía. Un trabajazo para toda la vida",
      text: "Lo que aportas… es la verdad… me pone la piel de gallina. He ido poco a poco. Al principio me sentía como bipolar: momentos de súper anclaje y después me iba… y ahora siento que estoy diferente. Siento más paz… más armonía conmigo misma, más amorosa. Me vienen conceptos o palabras en el momento y poquito a poco lo voy integrando. Es un trabajazo para toda la vida. El mensaje final es súper sencillo: vuelve a ti y todo está en ti."
    },
    { 
      name: "Andrea", 
      highlight: "Punto cero en medio del caos",
      text: "Para mí ha sido… en momentos en los que me empezaba a sentir mal o entrar un poquito en caos, entraba a punto cero. Conscientemente: cómo cuidar mi geometría, estar presente. Estoy muy agradecida, Herminio… de verdad espero seguir aprendiendo de ti. Eres un gran maestro."
    },
    { 
      name: "Ana", 
      highlight: "Me ha ido súper bien. La energía queda abierta",
      text: "A mí el formato de tiempo… trabajando me ha ido muy bien. Me ha ido bien que hubiera hoy una clase de dudas. Quizá lo único que añadiría sería algún encuentro más de aquí un tiempo para compartir… pero me ha ido súper bien. Y también decir que me ha dado sentir la plataforma donde estábamos todas… y me encanta que esta energía esté aquí abierta, como que estemos ahí en conexión."
    }
  ];

  // Auto-play for testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % facilitadorasTestimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, facilitadorasTestimonials.length]);

  const goToTestimonial = (index) => {
    setTestimonialIndex(index);
    setIsAutoPlaying(false);
  };

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % facilitadorasTestimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + facilitadorasTestimonials.length) % facilitadorasTestimonials.length);
    setIsAutoPlaying(false);
  };

  useEffect(() => {
    fetchContent();
  }, [courseSlug]);

  const fetchContent = async () => {
    try {
      const response = await api.get(`/special-courses/${courseSlug}/landing-content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching landing content:', error);
      if (error.response?.status === 404) {
        setError('not_available');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if enrollments are open
  const isOpen = content?._visibility?.is_open !== false;

  const handleEnroll = (plan, paymentType) => {
    if (!isOpen) {
      return; // Enrollment closed
    }
    if (!isAuthenticated) {
      navigate(`/login?redirect=/formaciones/${courseSlug}/inscripcion?plan=${plan}&payment=${paymentType}`);
      return;
    }
    navigate(`/formaciones/${courseSlug}/inscripcion?plan=${plan}&payment=${paymentType}`);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  // Show message if course is not available (closed/archived and user not enrolled)
  if (error === 'not_available') {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[hsl(var(--sage-light))]">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-16 h-16 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[hsl(var(--accent))]" />
          </div>
          <h1 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-4">
            Formación no disponible
          </h1>
          <p className="text-[hsl(var(--text-secondary))] mb-6">
            Esta formación no está disponible actualmente. Si ya estás inscrito/a, 
            accede con tu cuenta para ver el contenido.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/login')} className="btn-gold">
              Iniciar sesión
            </Button>
            <Button onClick={() => navigate('/formaciones')} variant="outline">
              Ver otras formaciones
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!content || error) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-[hsl(var(--text-secondary))]">Error al cargar el contenido</p>
      </div>
    );
  }

  // Navigation items for the secondary nav
  const navItems = [
    { id: 'hero', label: 'Inicio' },
    { id: 'para-quien', label: '¿Para quién?' },
    { id: 'contenido', label: 'Contenido' },
    { id: 'estructura', label: 'Estructura' },
    { id: 'inversion', label: 'Inversión' },
    { id: 'testimonios', label: 'Testimonios' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for fixed nav
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="pt-20">
      {/* Secondary Navigation Bar - Like Límites */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[hsl(var(--border))] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Back to formations */}
            <Link 
              to="/formaciones" 
              className="flex items-center gap-1 text-sm text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Formaciones</span>
            </Link>

            {/* Course nav - scrollable on mobile */}
            <nav className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors rounded-full whitespace-nowrap text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--sage-light))]/50"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* CTA */}
            <button
              onClick={() => scrollToSection('inversion')}
              className="hidden sm:flex items-center gap-2 bg-[#8a5a7a] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#7a4a6a] transition-all shadow-md"
            >
              Reservar plaza
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white border-t border-[hsl(var(--border))] p-3 shadow-2xl">
        <button
          onClick={() => scrollToSection('inversion')}
          className="w-full bg-[#8a5a7a] text-white py-4 rounded-full text-base font-medium hover:bg-[#7a4a6a] transition-all flex items-center justify-center gap-2 shadow-lg"
        >
          Reservar plaza
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-[90vh] flex items-center mt-14">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${content.hero.background_image || 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1776874282/landing_facilitadoras_lihwzr.png'})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#8a5a7a]/95 via-[#8a5a7a]/80 to-[#8a5a7a]/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
              <span className="text-sm text-white/90">{content.hero.badge_text}</span>
            </div>
            
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-4 leading-tight"
              data-testid="facilitadoras-title"
            >
              {content.hero.title}
            </h1>
            
            <p className="text-xl text-[hsl(var(--accent))] mb-6 font-heading">
              {content.hero.date_range}
            </p>

            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              {content.hero.description}
            </p>

            <blockquote className="border-l-4 border-[hsl(var(--accent))] pl-6 mb-8">
              <p className="text-white/90 italic text-lg">
                "{content.hero.quote}"
              </p>
            </blockquote>

            <div className="flex flex-wrap gap-6 mb-10">
              {content.hero.features.map((feature, idx) => {
                const IconComponent = iconMap[feature.icon] || Monitor;
                return (
                  <div key={idx} className="flex items-center gap-2 text-white/70">
                    <IconComponent className="w-5 h-5" strokeWidth={1.5} />
                    <span>{feature.text}</span>
                  </div>
                );
              })}
            </div>

            {isOpen ? (
              <Button 
                onClick={() => scrollToSection('inversion')}
                className="btn-gold text-lg px-8 py-6"
                data-testid="facilitadoras-cta-hero"
              >
                {content.hero.cta_text}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                <Lock className="w-5 h-5 text-white/70" />
                <span className="text-white/90">Inscripciones cerradas</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sostén Introduction */}
      <section id="para-quien" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="decorative-line mb-8" />
          <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-8">
            {content.introduction.title}
          </h2>
          
          <div className="prose prose-lg text-[hsl(var(--text-secondary))] leading-relaxed space-y-6">
            {content.introduction.paragraphs.map((para, idx) => (
              <p key={idx} className={idx === 1 ? "text-xl text-[hsl(var(--accent))] font-heading" : ""}>
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* El Sostén empieza en ti */}
      <section className="py-24 bg-[hsl(var(--sage-light))]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-8">
            {content.self_support.title}
          </h2>
          
          <p className="text-lg text-[hsl(var(--text-secondary))] mb-8">
            {content.self_support.intro_text}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {content.self_support.benefits.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-5 rounded-xl bg-white shadow-soft">
                <div className="w-8 h-8 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={2} />
                </div>
                <p className="text-[hsl(var(--text-secondary))]">{item}</p>
              </div>
            ))}
          </div>
          
          <p className="text-[hsl(var(--text-secondary))] italic border-l-4 border-[hsl(var(--sage))] pl-6">
            {content.self_support.closing_text}
          </p>
        </div>
      </section>

      {/* Estructura de la Formación */}
      <section id="estructura" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
              {content.structure.title}
            </h2>
            <p className="text-[hsl(var(--text-secondary))]">
              {content.structure.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Online */}
            <div className="card-soft border-2 border-[hsl(var(--accent))]/20 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                  <Monitor className="w-7 h-7 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-xl font-heading text-[hsl(var(--text-primary))]">
                    {content.structure.online.title}
                  </h3>
                  <span className="text-sm text-[hsl(var(--accent))] font-medium">
                    {content.structure.online.badge}
                  </span>
                </div>
              </div>
              
              <p className="text-[hsl(var(--text-secondary))] mb-6">
                {content.structure.online.description}
              </p>
              
              <ul className="space-y-3 mb-6">
                {content.structure.online.features.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[hsl(var(--text-secondary))]">
                    <Check className="w-4 h-4 text-[hsl(var(--sage))] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="p-4 rounded-xl bg-[hsl(var(--accent))]/5 border border-[hsl(var(--accent))]/20">
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  <strong className="text-[hsl(var(--accent))]">📌 Novedad en esta edición:</strong> {content.structure.online.note}
                </p>
              </div>
            </div>

            {/* Presencial */}
            <div className="card-soft border-2 border-[hsl(var(--sage))]/30 p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--sage))]/20 flex items-center justify-center">
                  <HandHeart className="w-7 h-7 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-xl font-heading text-[hsl(var(--text-primary))]">
                    {content.structure.presencial.title}
                  </h3>
                  <span className="text-sm text-[hsl(var(--sage))] font-medium">
                    {content.structure.presencial.badge}
                  </span>
                </div>
              </div>
              
              <p className="text-[hsl(var(--text-secondary))] mb-6">
                {content.structure.presencial.description}
              </p>
              
              <ul className="space-y-3 mb-6">
                {content.structure.presencial.features.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[hsl(var(--text-secondary))]">
                    <Check className="w-4 h-4 text-[hsl(var(--sage))] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="p-4 rounded-xl bg-[hsl(var(--sage))]/10">
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  <strong className="text-[hsl(var(--sage))]">Nota:</strong> {content.structure.presencial.note}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué vas a aprender? */}
      <section id="contenido" className="py-24 bg-[hsl(var(--sage-light))]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-12 text-center">
            {content.learning.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {content.learning.items.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-white">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Star className="w-3 h-3 text-[hsl(var(--accent))]" />
                </div>
                <p className="text-[hsl(var(--text-secondary))] text-sm">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fechas y Horarios */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-12 text-center">
            {content.dates.title}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Online Dates */}
            <div className="card-soft">
              <div className="flex items-center gap-3 mb-6">
                <Monitor className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                <h3 className="text-lg font-heading text-[hsl(var(--text-primary))]">
                  {content.dates.online.title}
                </h3>
              </div>
              <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
                {content.dates.online.schedule}
              </p>
              
              <div className="space-y-2 text-sm">
                {content.dates.online.months.map((month, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-[hsl(var(--sage-light))]">
                    <p className="font-medium text-[hsl(var(--text-primary))]">{month.name}</p>
                    <p className="text-[hsl(var(--text-secondary))]">{month.days}</p>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-[hsl(var(--text-tertiary))] mt-4 italic">
                {content.dates.online.note}
              </p>
            </div>

            {/* Presencial Dates */}
            <div className="card-soft">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                <h3 className="text-lg font-heading text-[hsl(var(--text-primary))]">
                  {content.dates.presencial.title}
                </h3>
              </div>
              <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
                {content.dates.presencial.schedule}
              </p>
              
              <div className="space-y-3">
                {content.dates.presencial.events.map((event, i) => (
                  <div key={i} className="p-4 rounded-lg bg-[hsl(var(--sage-light))] flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--sage))]/20 flex items-center justify-center">
                      <span className="text-[hsl(var(--sage))] font-medium">{i + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-[hsl(var(--text-primary))]">{event.dates}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 rounded-lg bg-[hsl(var(--accent))]/5">
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  <Clock className="w-4 h-4 inline mr-2 text-[hsl(var(--accent))]" />
                  {content.dates.presencial.hours}
                </p>
              </div>
              
              <p className="text-xs text-[hsl(var(--text-tertiary))] mt-4 italic">
                {content.dates.presencial.note}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Precios */}
      <section id="inversion" className="py-24 bg-gradient-to-b from-[hsl(var(--sage-light))] to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
              {content.pricing.title}
            </h2>
            <p className="text-[hsl(var(--text-secondary))] max-w-2xl mx-auto text-sm">
              {content.pricing.subtitle}
            </p>
          </div>

          {/* Enrollment Closed Banner */}
          {!isOpen && (
            <div className="mb-8 p-6 rounded-2xl bg-orange-50 border-2 border-orange-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-orange-800">Inscripciones cerradas</h3>
                  <p className="text-sm text-orange-700">
                    Las inscripciones para esta edición están cerradas. Si ya estás inscrito/a, 
                    puedes acceder a tu contenido desde tu área de alumno/a.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className={`space-y-8 ${!isOpen ? 'opacity-60 pointer-events-none' : ''}`}>
            {/* ONLINE */}
            <div className="card-soft border-2 border-[hsl(var(--border))] overflow-hidden">
              <div className="p-6 bg-[hsl(var(--sage-light))] border-b border-[hsl(var(--border))]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                    <Monitor className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading text-[hsl(var(--text-primary))]">
                      {content.pricing.online.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--text-secondary))]">
                      {content.pricing.online.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pronto Pago */}
                  <div className="p-6 rounded-2xl bg-white border-2 border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[hsl(var(--sage))] bg-[hsl(var(--sage))]/10 px-3 py-1 rounded-full">
                        Pronto pago
                      </span>
                      <span className="text-xs text-[hsl(var(--text-tertiary))]">Pago único</span>
                    </div>
                    <p className="text-4xl font-heading text-[hsl(var(--text-primary))] mb-2">{content.pricing.online.pronto_pago.price}</p>
                    <p className="text-sm text-[hsl(var(--sage))] mb-6">{content.pricing.online.pronto_pago.savings}</p>
                    <Button 
                      onClick={() => handleEnroll('online', 'pronto_pago')}
                      className="w-full btn-gold"
                    >
                      Inscribirme · {content.pricing.online.pronto_pago.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  
                  {/* Cuotas */}
                  <div className="p-6 rounded-2xl bg-white border-2 border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 px-3 py-1 rounded-full">
                        Pago fraccionado
                      </span>
                      <span className="text-xs text-[hsl(var(--text-tertiary))]">{content.pricing.online.cuotas.installments} mensualidades</span>
                    </div>
                    <p className="text-4xl font-heading text-[hsl(var(--text-primary))] mb-2">{content.pricing.online.cuotas.price}</p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] mb-6">{content.pricing.online.cuotas.total}</p>
                    <Button 
                      onClick={() => handleEnroll('online', 'cuotas')}
                      className="w-full btn-outline"
                    >
                      Inscribirme · {content.pricing.online.cuotas.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* PRESENCIAL */}
            <div className="card-soft border-2 border-[hsl(var(--border))] overflow-hidden">
              <div className="p-6 bg-[hsl(var(--sage-light))] border-b border-[hsl(var(--border))]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--sage))]/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading text-[hsl(var(--text-primary))]">
                      {content.pricing.presencial.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--text-secondary))]">
                      {content.pricing.presencial.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pronto Pago */}
                  <div className="p-6 rounded-2xl bg-white border-2 border-[hsl(var(--border))] hover:border-[hsl(var(--sage))] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[hsl(var(--sage))] bg-[hsl(var(--sage))]/10 px-3 py-1 rounded-full">
                        Pronto pago
                      </span>
                      <span className="text-xs text-[hsl(var(--text-tertiary))]">Pago único</span>
                    </div>
                    <p className="text-4xl font-heading text-[hsl(var(--text-primary))] mb-2">{content.pricing.presencial.pronto_pago.price}</p>
                    <p className="text-sm text-[hsl(var(--sage))] mb-6">{content.pricing.presencial.pronto_pago.savings}</p>
                    <Button 
                      onClick={() => handleEnroll('presencial', 'pronto_pago')}
                      className="w-full btn-sage"
                    >
                      Inscribirme · {content.pricing.presencial.pronto_pago.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  
                  {/* Cuotas */}
                  <div className="p-6 rounded-2xl bg-white border-2 border-[hsl(var(--border))] hover:border-[hsl(var(--sage))] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 px-3 py-1 rounded-full">
                        Pago fraccionado
                      </span>
                      <span className="text-xs text-[hsl(var(--text-tertiary))]">{content.pricing.presencial.cuotas.installments} mensualidades</span>
                    </div>
                    <p className="text-4xl font-heading text-[hsl(var(--text-primary))] mb-2">{content.pricing.presencial.cuotas.price}</p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] mb-6">{content.pricing.presencial.cuotas.total}</p>
                    <Button 
                      onClick={() => handleEnroll('presencial', 'cuotas')}
                      className="w-full btn-outline"
                    >
                      Inscribirme · {content.pricing.presencial.cuotas.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* PACK COMPLETO */}
            <div className="card-soft border-2 border-[hsl(var(--accent))] overflow-hidden relative">
              {content.pricing.pack.recommended && (
                <div className="absolute top-0 right-0 bg-[hsl(var(--accent))] text-white text-sm px-4 py-2 rounded-bl-xl font-medium">
                  ⭐ Recomendado
                </div>
              )}
              
              <div className="p-6 bg-gradient-to-r from-[hsl(var(--accent))]/10 to-[hsl(var(--sage))]/10 border-b border-[hsl(var(--accent))]/20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))]/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading text-[hsl(var(--text-primary))]">
                      {content.pricing.pack.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--text-secondary))]">
                      {content.pricing.pack.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pronto Pago */}
                  <div className="p-6 rounded-2xl bg-white border-2 border-[hsl(var(--accent))]/30 hover:border-[hsl(var(--accent))] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[hsl(var(--sage))] bg-[hsl(var(--sage))]/10 px-3 py-1 rounded-full">
                        Pronto pago
                      </span>
                      <span className="text-xs text-[hsl(var(--text-tertiary))]">Pago único</span>
                    </div>
                    <p className="text-4xl font-heading text-[hsl(var(--accent))] mb-2">{content.pricing.pack.pronto_pago.price}</p>
                    <p className="text-sm text-[hsl(var(--sage))] mb-6">{content.pricing.pack.pronto_pago.savings}</p>
                    <Button 
                      onClick={() => handleEnroll('pack', 'pronto_pago')}
                      className="w-full btn-gold"
                    >
                      Inscribirme · {content.pricing.pack.pronto_pago.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  
                  {/* Cuotas */}
                  <div className="p-6 rounded-2xl bg-white border-2 border-[hsl(var(--accent))]/30 hover:border-[hsl(var(--accent))] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 px-3 py-1 rounded-full">
                        Pago fraccionado
                      </span>
                      <span className="text-xs text-[hsl(var(--text-tertiary))]">{content.pricing.pack.cuotas.installments} mensualidades</span>
                    </div>
                    <p className="text-4xl font-heading text-[hsl(var(--accent))] mb-2">{content.pricing.pack.cuotas.price}</p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] mb-6">{content.pricing.pack.cuotas.total}</p>
                    <Button 
                      onClick={() => handleEnroll('pack', 'cuotas')}
                      className="w-full btn-outline border-[hsl(var(--accent))] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"
                    >
                      Inscribirme · {content.pricing.pack.cuotas.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="card-soft bg-[hsl(var(--sage-light))] p-6 mt-8">
            <h3 className="font-heading text-[hsl(var(--text-primary))] mb-4">
              {content.pricing.payment_info.title}
            </h3>
            <ul className="space-y-2 text-sm text-[hsl(var(--text-secondary))]">
              {content.pricing.payment_info.items.map((item, idx) => (
                <li key={idx}>• {item.startsWith('Paga') || item.startsWith('Métodos') ? <strong>{item.split(':')[0]}:</strong> : ''}{item.startsWith('Paga') || item.startsWith('Métodos') ? item.split(':')[1] : item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonios - Carrusel */}
      <section id="testimonios" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm text-[hsl(var(--accent))] font-medium mb-3">Voces de alumnas</p>
            <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4">
              Lo que dicen quienes ya han pasado
            </h2>
            <div className="w-16 h-[2px] bg-[hsl(var(--accent))] mx-auto" />
          </div>
          
          {/* Carousel */}
          <div className="relative px-4 lg:px-12">
            {/* Arrows */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[hsl(var(--accent))]/30 flex items-center justify-center hover:bg-[hsl(var(--accent))]/10 transition-colors z-10"
              aria-label="Anterior testimonio"
            >
              <ChevronLeft className="w-5 h-5 text-[hsl(var(--accent))]" />
            </button>
            <button 
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[hsl(var(--accent))]/30 flex items-center justify-center hover:bg-[hsl(var(--accent))]/10 transition-colors z-10"
              aria-label="Siguiente testimonio"
            >
              <ChevronRight className="w-5 h-5 text-[hsl(var(--accent))]" />
            </button>

            {/* Cards container */}
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500"
                style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
              >
                {facilitadorasTestimonials.map((t, i) => (
                  <div key={i} className="w-full flex-shrink-0">
                    <div className="bg-[hsl(var(--sage-light))]/40 p-8 lg:p-12 text-center">
                      <div className="w-14 h-14 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mx-auto mb-6">
                        <Quote className="w-6 h-6 text-[hsl(var(--accent))]" />
                      </div>
                      <p className="text-[hsl(var(--accent))] font-medium mb-4">"{t.highlight}"</p>
                      <p className="text-lg text-[hsl(var(--text-secondary))] italic leading-relaxed max-w-2xl mx-auto mb-8">
                        "{t.text}"
                      </p>
                      <div className="inline-flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(var(--accent))]/30 to-[hsl(var(--accent))]/10" />
                        <div className="text-left">
                          <p className="font-medium text-[hsl(var(--text-primary))]">{t.name}</p>
                          <p className="text-sm text-[hsl(var(--accent))]">Alumna de la formación</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex justify-center gap-3 mt-8">
              {facilitadorasTestimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToTestimonial(i)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === testimonialIndex ? 'bg-[hsl(var(--accent))] w-8' : 'bg-gray-300 w-4'
                  }`}
                  aria-label={`Ir al testimonio ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sobre el Instructor */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-1">
              <div className="aspect-square rounded-3xl overflow-hidden bg-[hsl(var(--sage-light))]">
                <img 
                  src={content.instructor.image}
                  alt={content.instructor.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mt-6 text-center">
                {content.instructor.name}
              </h3>
              <p className="text-sm text-[hsl(var(--text-secondary))] text-center">
                {content.instructor.role}
              </p>
            </div>
            
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-6">
                {content.instructor.title}
              </h2>
              
              <div className="prose prose-sm text-[hsl(var(--text-secondary))] space-y-4">
                {content.instructor.paragraphs.map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
                {content.instructor.highlight_text && (
                  <p className="italic border-l-4 border-[hsl(var(--accent))] pl-4">
                    {content.instructor.highlight_text}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[hsl(var(--sage-light))]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h2 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-12 text-center">
            {content.faqs.title}
          </h2>
          
          <div className="space-y-4">
            {content.faqs.items.map((faq, index) => (
              <div 
                key={index}
                className="card-soft overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium text-[hsl(var(--text-primary))]">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[hsl(var(--text-tertiary))]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[hsl(var(--text-tertiary))]" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-[hsl(var(--text-secondary))] text-sm">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#8a5a7a] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-heading mb-6">
            {content.final_cta.title}
          </h2>
          <p className="text-white/70 text-lg mb-4 max-w-2xl mx-auto">
            {content.final_cta.description}
          </p>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto font-medium">
            {content.final_cta.highlight}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isOpen ? (
              <Button 
                onClick={() => scrollToSection('inversion')}
                className="bg-white hover:bg-white/90 text-[#8a5a7a] px-8 py-6 text-lg font-medium"
                data-testid="facilitadoras-cta-final"
              >
                {content.final_cta.cta_text}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                <Lock className="w-5 h-5 text-white/70" />
                <span className="text-white/90">Inscripciones cerradas</span>
              </div>
            )}
            
            <a 
              href={`https://wa.me/${content.final_cta.whatsapp_number}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-6 rounded-full border-2 border-white/30 text-white hover:bg-white/10 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              {content.final_cta.whatsapp_text}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FacilitadorasLanding;
